import { GetSearchPreviewResponse } from 'components/Header/components/TopRow/components/SearchProducts/interfaces';
import { createAPIConnection } from 'services';
import { getStore } from 'stores/helpers';
import {
	GetSearchPreviewRequest,
	GetReleaseProductsRequest,
	GetSearchedProductsResponse,
	GetCategoryProductsRequest,
	GetPromotionInfoResponse,
	GetPromotionProductsRequest,
	GetCategoryProductsResponse,
	AddSuggestionProductRequest,
	ProductsDetailsResponse,
	ProductsDetailsRequest,
	GetMenuFlagsResponse,
	AddSuggestionProductsResponse,
	GetSearchedProductsDrawerPromotionResponse,
	GetDistributorsPromotionReponse,
	GenericPaginatedProductsResponse,
	GetBrandProductsResponse,
	GetBrandsResponse,
	GetBrandsRequest,
	GetProductDetailsResponse,
	GetProductDetailsRequest,
	GetPromotionsByProductRequest,
	GetPromotionsByProductResponse,
	GetProductImagesRequest,
	GetProductImagesResponse,
	GetProductDescriptionRequest,
	GetProductDescriptionResponse,
	GetCampaignRequest,
	GetBrandProductsRequest,
	GetSearchedProductsRequest,
	GetSuggestionProductsRequest,
} from './interfaces';
import { requestHandler } from '../helpers';

const api = createAPIConnection('/products', {
	onRequest(config) {
		const provider = getStore().customerReducer?.provider;

		if (provider !== 'PARCEIRO_HYPERA') Object.assign(config?.headers || {}, { 'Partner-origin': provider });
	},
});

export const getSearchPreview = async (data: GetSearchPreviewRequest) => {
	return requestHandler<GetSearchPreviewResponse>(api.post('/preview', data));
};

export const getReleaseProducts = async (data: GetReleaseProductsRequest) => {
	return requestHandler<GenericPaginatedProductsResponse>(api.post('/release', data));
};

export const getReleaseProductsPage = async (data: GetReleaseProductsRequest) => {
	return requestHandler<GenericPaginatedProductsResponse>(api.post('/release', data));
};

export const getSearchedProducts = async (data: GetSearchedProductsRequest) => {
	const dataCopy = { ...data };

	if (!dataCopy?.facets) delete dataCopy.facets;

	return requestHandler<GetSearchedProductsResponse>(api.post(`/search/${data.posId}`, dataCopy));
};

export const getPromotionInfo = ({ posId, promotionId }: { posId: string; promotionId: string }) =>
	requestHandler<GetPromotionInfoResponse>(api.get(`/pos/${posId}/promotion/${promotionId}`));

export const getCategorieProducts = async (data: GetCategoryProductsRequest) => {
	const dataCopy = { ...data };
	if (!dataCopy?.facets) delete dataCopy.facets;

	return requestHandler<GetCategoryProductsResponse>(api.post('/category/search', dataCopy));
};

export const getPromotionProducts = async (data: GetPromotionProductsRequest) => {
	const dataCopy = { ...data };
	if (!dataCopy?.facets) delete dataCopy.facets;

	return requestHandler<GenericPaginatedProductsResponse>(api.post('/promotions', dataCopy));
};

export const getPromotionsByProduct = async ({
	posId,
	productId,
	order,
	page,
	size,
}: GetPromotionsByProductRequest) => {
	return requestHandler<GetPromotionsByProductResponse>(
		api.get(`/pos/${posId}/product/${productId}/promotions`, {
			params: {
				sort: order,
				page,
				size,
			},
		}),
	);
};

export const getSuggestionProduct = async (data: GetSuggestionProductsRequest) => {
	return requestHandler<GenericPaginatedProductsResponse>(api.post(`/suggestions`, data));
};

export const addSuggestionProduct = async (data: AddSuggestionProductRequest) => {
	return requestHandler<AddSuggestionProductsResponse>(api.post(`/suggestions/add-products`, data));
};

export const getProductsDetails = async ({ posId, products }: { posId: string } & ProductsDetailsRequest) => {
	return requestHandler<ProductsDetailsResponse>(api.post(`/pos/${posId}`, { products }));
};

export const getMenuFlags = async (posId: string) => {
	return requestHandler<GetMenuFlagsResponse>(api.get(`/pos/${posId}/validate`));
};

export const getSearchedProductsDrawerPromotion = async ({
	text,
	posId,
	promotionId,
}: {
	text: string;
	posId: string;
	promotionId: string;
}) => {
	return requestHandler<GetSearchedProductsDrawerPromotionResponse>(
		api.post(`/pos/${posId}/promotion/${promotionId}/search`, { text }),
	);
};

export const getDistributorsPromotion = async ({ posId, promotionId }: { posId: string; promotionId: string }) => {
	return requestHandler<GetDistributorsPromotionReponse>(api.get(`/distributor/pos/${posId}/promotion/${promotionId}`));
};

export const getBrandProducts = async (data: GetBrandProductsRequest) => {
	const dataCopy = { ...data };

	if (!dataCopy?.facets) delete dataCopy.facets;

	return requestHandler<GetBrandProductsResponse>(api.post('/brand/search', dataCopy));
};

export const getProductBrands = async ({ posId, signal, ...params }: GetBrandsRequest) => {
	return requestHandler<GetBrandsResponse>(api.post(`/pos/${posId}/brands`, { ...params }, { signal }));
};

export const getProductDetails = async ({ posId, productId }: GetProductDetailsRequest) => {
	return requestHandler<GetProductDetailsResponse>(api.get(`/pos/${posId}/product/${productId}`));
};

export const getProductImages = async ({ productId }: GetProductImagesRequest) => {
	return requestHandler<GetProductImagesResponse>(api.get(`/${productId}/images`));
};

export const getProductDescription = async ({ productId }: GetProductDescriptionRequest) => {
	return requestHandler<GetProductDescriptionResponse>(api.get(`/${productId}/description`));
};

export const getCampaign = async ({ campaignCode, posId }: GetCampaignRequest) => {
	return requestHandler<GetPromotionInfoResponse>(api.get(`/pos/${posId}/campaign/${campaignCode}`));
};
